import type { VodItem } from '@player/domain';
import { FONT_STACK, colors } from '../theme.js';
import { useTranslator } from '../i18n/I18nProvider.js';

/** Una fascia di On demand: un titolo e le sue locandine. */
export interface PhoneVodRow {
  readonly id: string;
  readonly title: string;
  readonly items: ReadonlyArray<VodItem>;
}

/**
 * On demand sul telefono: **due etichette in cima**, poi le fasce.
 *
 * ## Due etichette, non due schede
 *
 * `Film` e `Serie TV` stanno **dentro** questa scheda e non sono due voci della barra in basso: la
 * barra ne regge cinque, e con una sesta la differenza fra film e serie — che è una distinzione
 * interna a «cosa guardo stasera» — prenderebbe lo stesso peso di «cerca» e «opzioni»
 * (`04-SCHERMATE-MOBILE.md` §5).
 *
 * ## I generi si aprono, non si srotolano
 *
 * L'elenco completo è una schermata a sé, con il suo campo per filtrare: quaranta generi srotolati
 * qui spingerebbero le fasce fuori dallo schermo, e le fasce sono quel che si guarda per primo.
 *
 * ## Le locandine sono verticali
 *
 * `2 / 3`, e non la proporzione dei canali: una locandina stirata in orizzontale si riconosce molto
 * peggio, e su un telefono il riconoscimento a colpo d'occhio è l'unica navigazione che resta.
 */
export function PhoneVodScreen({
  kind,
  onPickKind,
  rows,
  onOpenItem,
  onOpenGenres,
}: {
  readonly kind: 'movies' | 'series';
  readonly onPickKind: (kind: 'movies' | 'series') => void;
  readonly rows: ReadonlyArray<PhoneVodRow>;
  readonly onOpenItem: (itemId: string) => void;
  readonly onOpenGenres: () => void;
}) {
  const translator = useTranslator();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20, padding: '8px 0 28px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 16, padding: '0 20px' }}>
        {(['movies', 'series'] as const).map((quale) => (
          <button
            key={quale}
            type="button"
            onClick={() => onPickKind(quale)}
            aria-pressed={quale === kind}
            style={{
              border: 0,
              background: 'none',
              padding: '4px 0',
              cursor: 'pointer',
              /*
                L'etichetta scelta è grande e bianca, l'altra più piccola e grigia — come nel
                prototipo. Non sono due pulsanti uguali con uno sfondo diverso: la gerarchia dice
                quale elenco si sta guardando anche con la coda dell'occhio.
              */
              font: `800 ${quale === kind ? 28 : 20}px ${FONT_STACK}`,
              letterSpacing: '-.02em',
              color: quale === kind ? colors.text : colors.textFaint,
            }}
          >
            {translator.t(quale === 'movies' ? 'menu.movies' : 'menu.series')}
          </button>
        ))}

        <button
          type="button"
          onClick={onOpenGenres}
          style={{
            marginLeft: 'auto',
            minHeight: 44,
            border: 0,
            background: 'none',
            color: colors.accent,
            font: `600 14px ${FONT_STACK}`,
            cursor: 'pointer',
            padding: 0,
          }}
        >
          {translator.t('phone.genres.title')}
        </button>
      </div>

      {rows.length === 0 && (
        // Un catalogo vuoto si dice. Una schermata bianca si legge come un guasto, e manda a
        // cercarlo dove non c'è.
        <p style={{ margin: 0, padding: '20px', font: `500 15px ${FONT_STACK}`, color: colors.textDim }}>
          {translator.t('phone.vod.empty')}
        </p>
      )}

      {rows.map((row) => (
        <section key={row.id} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <h2 style={{ margin: 0, padding: '0 20px', font: `700 18px ${FONT_STACK}` }}>{row.title}</h2>
          <div
            style={{
              display: 'flex',
              gap: 10,
              overflowX: 'auto',
              scrollbarWidth: 'none',
              padding: '0 20px',
            }}
          >
            {row.items.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => onOpenItem(item.id)}
                style={{
                  flex: 'none',
                  width: 110,
                  border: 0,
                  background: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  color: colors.text,
                  textAlign: 'left',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6,
                }}
              >
                <span
                  aria-hidden
                  style={{
                    width: '100%',
                    aspectRatio: '2 / 3',
                    borderRadius: 10,
                    background: colors.surface,
                    backgroundImage:
                      item.posterUrl !== null && item.posterUrl !== undefined
                        ? `url(${item.posterUrl})`
                        : undefined,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                />
                <span
                  style={{
                    font: `600 13px ${FONT_STACK}`,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {item.title}
                </span>
              </button>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
